export type MicConnectionType = 'wired' | 'wireless';

export type WirelessTransmitterType = 'handheld' | 'beltpack_clip' | 'beltpack_jack' | 'headset' | 'other';

export type MicStandType = 'none' | 'small' | 'medium' | 'big' | 'round_base' | 'clip';

export const STAND_LABELS: Record<MicStandType, string> = {
  none: 'None',
  small: 'Small',
  medium: 'Medium',
  big: 'Big',
  round_base: 'Round base',
  clip: 'Clip',
};

/**
 * A REAL mic stand (something the venue/band must physically supply).
 * `'none'` = no stand at all; `'clip'` = a CLIP-ON mounting that goes WITH the
 * microphone and by definition needs NO separate stand (and no boom). A `clip`
 * must therefore NEVER be counted as a stand — not in the MIC STANDS table, not
 * in the "N stands provided by…" counters (rider), not in the per-person config
 * recap — and must never print a boom mention. Single source of truth for that
 * rule, used by RiderPage and by `aggregateVenueMicStands` (lib/micStands.ts).
 */
export function isRealMicStand(stand: MicStandType | undefined | null): stand is MicStandType {
  return !!stand && stand !== 'none' && stand !== 'clip';
}

export type ProvidedBy = 'band' | 'venue';

export const PROVIDED_BY_LABELS: Record<ProvidedBy, string> = {
  band: 'The Band',
  venue: 'The Venue',
};

export type TrackDirection = 'input' | 'output';

export interface Track {
  id: string;
  name: string;           // e.g. "Kick", "Snare", "Overhead L"
  micType: string;        // e.g. "Beta 58", "DI Box", "SM57"
  connectionType: MicConnectionType;  // 'wired' or 'wireless'
  transmitterType?: WirelessTransmitterType;  // Only if wireless
  transmitterNotes?: string;  // e.g. "Shure ULXD4Q", specific model
  channel: number;        // Channel number within the instrument (1, 2, 3...)
  inputListChannel: number;  // Fixed channel number in the input list (console input)
  direction: TrackDirection; // 'input' = source into the mix (mic/DI), 'output' = return/IEM/send
  rackId: string;         // Named I/O rack this track patches into (see SpecsConfig.stageRacks)
  ioChannel: number;      // Channel number ON the selected rack (independent of inputListChannel)
  notes: string;          // General remarks
  stand?: MicStandType;   // Mic stand type: 'none' = no stand
  withoutBoom?: boolean;  // true = stand without boom (default false = with boom)
  /** Free-text DESTINATION of the (venue-provided) stand — i.e. for WHOM /
   *  where the stand is. When EMPTY the rider's Mic Stands table DERIVES it
   *  from the carrier of the instrument (the musician/crew member's name, in
   *  musician order). When filled it OVERRIDES that derived value for this
   *  track. (2026-10-07, v0.651) */
  destination?: string;
  providedBy?: ProvidedBy; // 'band' = we bring the mic, 'venue' = venue provides it
  micProvidedBy?: ProvidedBy; // same choice for the MICROPHONE itself (distinct from the stand)
  /** INPUT-LIST PATCH MARK (2026-10-23, user): a colour chosen per track — the
   *  rider PDF tints the track's Ch CELL with it, and the Input List section
   *  prints a legend (per band, editable) explaining what each colour means
   *  (e.g. "red = patch on the house stagebox"). Empty = no mark. */
  patchMark?: string;
  /** TRUE = this track's rack channel / console input is SHARED with a
   *  replacement or guest who plays the same instrument (a dep trombonist
   *  covers the main player, they never play at the same time), so the
   *  occupancy pickers must NOT block a channel already used by that person.
   *  Default FALSE = strict occupancy: ANY channel already patched by someone
   *  else (including this person's own other tracks) is greyed out. (2026-09-20) */
  sharedWithReplacement?: boolean;
}

export function createTrack(overrides?: Partial<Track>): Track {
  return {
    id: crypto.randomUUID(),
    name: '',
    micType: '',
    connectionType: 'wired',
    transmitterType: undefined,
    transmitterNotes: '',
    channel: 0,
    inputListChannel: 0,
    direction: 'input',
    rackId: '',
    ioChannel: 0,
    notes: '',
    stand: 'none',
    withoutBoom: false,
    providedBy: 'band',
    micProvidedBy: 'band',
    sharedWithReplacement: false,
    ...overrides,
  };
}

export interface InstrumentWireless {
  /** The instrument's "needs a wireless transmitter" switch. SINGLE-FLAG MODEL
   *  (2026-09-29, décision « un seul drapeau »): on the CARD (`instrument.wireless`)
   *  this is THE switch — the rider (preview, layout editor, PDF, promoter share
   *  page) follows it and nothing else. The SAME type is reused for the
   *  per-transport `specs.<transport>.wirelessTransmitters[id]` entry, where
   *  `enabled` is INERT/OBSOLETE: kept for backward compatibility of stored
   *  blobs, never read and never written by the app there. */
  enabled?: boolean;
  transmitterType?: WirelessTransmitterType;
  notes?: string;        // model e.g. "Shure ULXD4Q"
  /** Who provides the microphone/capsule: 'band' (we bring it) or 'venue'.
   *  Distinct from the receiver — a band may own a special capsule but ask the
   *  venue for a standard receiver. Default 'band'. (2026-08-21, Defaults-Spec C1) */
  micProvider?: ProvidedBy;
  /** Who provides the receiver/bodypack: 'band' (we bring it) or 'venue'.
   *  Default 'band'. (2026-08-21, Defaults-Spec C1) */
  receiverProvider?: ProvidedBy;
}

// createInstrumentWireless REMOVED (audit 2026-10-24 [DEAD]): never called —
// the wireless block is built inline in migrateAppConfig / defaults.

export interface Instrument {
  id: string;
  name: string;           // e.g. "MPC", "Drum Kit", "Guitar Rig"
  tracks: Track[];        // Multiple tracks per instrument (always WIRED — the signal
                          // into the console; a wireless transmitter, if any, lives on
                          // instrument.wireless and is a separate rider request).
  // Wireless transmitter for this instrument (guitar/bass/mic), NOT an input-list
  // channel — the signal still enters wired (DI/mic), the transmitter is extra.
  wireless?: InstrumentWireless;
  // Legacy fields for backward compatibility (optional)
  micType?: string;
  channel?: number;
  di?: boolean;
  stand?: boolean;
  standType?: string;
  notes?: string;
}

export function createInstrument(overrides?: Partial<Instrument>): Instrument {
  return {
    id: crypto.randomUUID(),
    name: '',
    tracks: [],
    wireless: undefined,
    // Legacy
    micType: '',
    channel: 0,
    di: false,
    stand: false,
    standType: '',
    notes: '',
    ...overrides,
  };
}

export type BacklineItemType =
  | 'mic_stand' | 'keyboard_stand' | 'guitar_stand' | 'amp' | 'keyboard'
  | 'drum_kit' | 'drum_stool' | 'drum_carpet' | 'spare_guitar' | 'spare_bass'
  | 'snare' | 'snare_stand' | 'rack_tom' | 'floor_tom' | 'hi_hat' | 'cymbal' | 'cymbal_stand' | 'bass_drum'
  | 'percussion' | 'cowbell' | 'tambourine' | 'congas' | 'djembe' | 'shaker'
  | 'chair' | 'other';

// Ordered dropdown options for a backline item's type (single source of truth
// for the musician edit page and any future backline pickers). Re-ordered here
// to put the drum/percussion detail first — a drummer's main kit.
export const BACKLINE_TYPE_OPTIONS: { value: BacklineItemType; label: string }[] = [
  { value: 'drum_kit', label: 'Drum kit' },
  { value: 'snare', label: 'Snare drum' },
  { value: 'snare_stand', label: 'Snare stand' },
  { value: 'rack_tom', label: 'Rack tom' },
  { value: 'floor_tom', label: 'Floor tom' },
  { value: 'bass_drum', label: 'Bass drum' },
  { value: 'hi_hat', label: 'Hi-hat' },
  { value: 'cymbal', label: 'Cymbal' },
  { value: 'cymbal_stand', label: 'Cymbal stand' },
  { value: 'drum_stool', label: 'Drum stool' },
  { value: 'drum_carpet', label: 'Drum carpet' },
  { value: 'percussion', label: 'Percussion' },
  { value: 'congas', label: 'Congas' },
  { value: 'djembe', label: 'Djembe' },
  { value: 'cowbell', label: 'Cowbell' },
  { value: 'tambourine', label: 'Tambourine' },
  { value: 'shaker', label: 'Shaker' },
  { value: 'keyboard', label: 'Keyboard' },
  { value: 'keyboard_stand', label: 'Keyboard stand' },
  { value: 'amp', label: 'Amp' },
  { value: 'guitar_stand', label: 'Guitar stand' },
  { value: 'mic_stand', label: 'Mic stand' },
  { value: 'spare_guitar', label: 'Spare guitar' },
  { value: 'spare_bass', label: 'Spare bass' },
  { value: 'chair', label: 'Chair' },
  { value: 'other', label: 'Other' },
];

export interface BacklineItem {
  id: string;
  type: string;         // free text now (editable app-wide suggestions; see BACKLINE_TYPE_OPTIONS for defaults)
  label: string;       // e.g. "Boom stand", "Keyboard stand X stand", "Guitar stand"
  quantity: number;
  notes: string;
  imageUrl?: string;   // optional reference image (data URL or upload URL) shown in the rider
  /** 'band' (default) = the band brings this item on tour; 'venue' = the venue
   * must provide it. A per-show override (riders.requirements.backlineProviders)
   * and plane/train transport can still flip the EFFECTIVE provider — see
   * `resolveBacklineProvider` in src/lib/backlineProvider.ts. */
  providedBy?: ProvidedBy;
}

export function createBacklineItem(overrides?: Partial<BacklineItem>): BacklineItem {
  return {
    id: crypto.randomUUID(),
    type: 'other',
    label: '',
    quantity: 1,
    notes: '',
    providedBy: 'band',
    ...overrides,
  };
}

export type DietType = 'none' | 'vegetarian' | 'vegan' | 'gluten_free' | 'lactose_free' | 'nut_allergy' | 'other';

export type CrewRole = 'crew' | 'driver' | 'foh' | 'guest' | 'lights' | 'manager' | 'mon' | 'other' | 'stagehand' | 'tour manager' | 'production manager' | 'video' | 'guitar tech' | 'drum tech' | 'keys tech';
export type ContactRole = string; // free-text tags, user-defined

export interface Musician {
  id: string;
  name: string;
  fonction: string; // internal — defaults to 'musician', free text, not shown on screen
  iemType: 'stereo' | 'mono' | 'none';
  /** true = wired in-ears (feed straight from the monitor rack): no transmitter
   *  and no bodypack to provide — excluded from the rider's "Units needed".
   *  `iemWiredProvidedBy` = WHO provides the wired pack (2026-10-17, user):
   *  'band' (default — the feed comes from the band's monitor rack) or 'venue'. */
  iemWired?: boolean;
  iemWiredProvidedBy?: ProvidedBy;
  wedges: number;
  subwoofer: boolean;
  // Monitor output routing: which rack and starting channel the IEMs / wedges
  // are patched out of. `wedgesStereo` = wedges carry a stereo L/R pair when
  // there is more than one; false = linked (same mono feed to each).
  iemRackId?: string;
  iemOutStart?: number;
  wedgeRackId?: string;
  wedgeOutStart?: number;
  wedgesStereo?: boolean;
  subRackId?: string;
  subOutStart?: number;
  /** PATCH MARKS for the monitor OUTPUTS (2026-10-23, user): a colour per
   *  return group — the Output List tints the Ch cell(s) of the IEM / wedges /
   *  sub rows with it (same colour = same destination, pooled into the legend). */
  iemPatchMark?: string;
  wedgePatchMark?: string;
  subPatchMark?: string;
  color: string;
  instruments: Instrument[];
  backline: BacklineItem[];
  diet: DietType;
  dietNotes: string;
  /** Non-food allergies (cats/dogs, latex, dust…) — mirrored from team_members (2026-08-26). */
  nonFoodAllergies?: string;
  towels: number;
  /** Dressing-room towels for this person (2026-08-07). */
  dressingRoomTowels: number;
  /** Water bottles for this person (side stage / dressing room) (2026-08-07). */
  water: number;
  active: boolean;
  order: number;
  // Extra roles for this band (e.g. 'tour manager' when the bassist also
  // manages the tour). Mirrors CrewMember.tags semantics (2026-08-07).
  tags?: string[];
  /** Base role of the musician in THIS band: main (permanent member),
   * session (deputy/substitute), guest (occasional invitation).
   * Default 'main'. (2026-08-09) */
  baseRole?: 'main' | 'session' | 'guest';
  // Personal Info (internal — not shown in the rider PDF)
  realName?: string;
  lastName?: string;
  address?: string;
  postCode?: string;
  locality?: string;
  country?: string;
  phone?: string;
  email?: string;
  various?: string;
}

export function createMusician(
  overrides?: Partial<Musician>,
  order?: number,
): Musician {
  return {
    id: crypto.randomUUID(),
    name: '',
    fonction: 'musician',
    baseRole: 'main',
    iemType: 'stereo',
    wedges: 0,
    subwoofer: false,
    color: '#e94560',
    instruments: [],
    backline: [],
    diet: 'none',
    dietNotes: '',
    nonFoodAllergies: '',
        towels: 1,
        dressingRoomTowels: 1,
        water: 0,
        active: true,
        order: order ?? 0,
        tags: [],
        realName: '',
        lastName: '',
        address: '',
        postCode: '',
        locality: '',
    country: '',
    phone: '',
    email: '',
    various: '',
    ...overrides,
  };
}

export interface ChannelConflict {
  musician1Name: string;
  musician2Name: string;
  channel: number;
  instrument1Name: string;
  instrument2Name: string;
}

export type TransportType = 'plane' | 'van' | 'tourbus' | 'train';

/**
 * Generic wrapper — per-transport presets for each section.
 * van / plane / tourbus / train.
 */
export type TransportConfig<T> = {
  van: T;
  plane: T;
  tourbus: T;
  train: T;
};

/**
 * Bundle of all config sections for one transport mode.
 * Replaces the old TransportConfig (now generic).
 */
export interface SectionBundle {
  sound: SoundConfig;
  iem: IemConfig;
  stage: StageConfig;
  hospitality: HospitalityConfig;
  lights: LightsConfig;
  backline: BacklineConfig;
  stageDesign: StageDesignConfig;
  general: GeneralConfig;
  specs: SpecsConfig;
}

export interface SoundConfig {
  /** Legal intro shown at the top of the Sound section in the rider PDF. */
  intro: string;
  fohConsole: string;
  /** What the band needs AT the FOH position (stable table/riser, power…). */
  fohRequirements: string;
  paSystem: string;
  /** Monitor CONSOLE (the desk): a band may tour with its own desk while asking
   *  the venue for the wedges/IEM — hence its own field and provider choice. */
  monitorConsole: string;
  /** What the band needs AT the monitor position (riser for the desk, power…). */
  monitorRequirements: string;
  monitorSystem: string;
  /** Sortie du MIX PRINCIPAL envoyé à la sono, choisie parmi les racks/tables
   *  déclarés dans la config (ex. « Wing Rack » sorties 1-2 = paire stéréo L/R).
   *  `paMixOutStart` = 1er canal de la paire (le 2ᵉ est outStart+1). */
  paMixRackId?: string;
  paMixOutStart?: number;
  /** Who provides each piece of gear: 'band' (we bring it) or 'venue' (we ask for it). */
  fohProvider?: 'band' | 'venue';
  paProvider?: 'band' | 'venue';
  monitorConsoleProvider?: 'band' | 'venue';
  monitorProvider?: 'band' | 'venue';
  /** Who provides what is needed AT the FOH position (power, table…) — venue
   *  by default: those are requests to the venue. (2026-10-18) */
  fohRequirementsProvider?: 'band' | 'venue';
  /** Same for the monitor-position requirements. */
  monitorRequirementsProvider?: 'band' | 'venue';
}

const DEFAULT_SOUND_INTRO = 'We require and insist on the exact equipment specified in this rider. If any item cannot be provided despite all reasonable efforts, please contact us well in advance of the show to discuss solutions. Substitutions will only be accepted if approved beforehand by our management and/or the tour\'s engineers.';

export function createDefaultSoundConfig(): SoundConfig {
  return { intro: DEFAULT_SOUND_INTRO, fohConsole: '', fohRequirements: '', paSystem: '', monitorConsole: '', monitorRequirements: '', monitorSystem: '', fohProvider: 'venue', paProvider: 'venue', monitorConsoleProvider: 'venue', monitorProvider: 'venue', fohRequirementsProvider: 'venue', monitorRequirementsProvider: 'venue' };
}

export interface IemConfig {
  system: string;
  /** Transmitter units (emitters — model defined in `system`). */
  units: number;
  /** Bodypack receivers needed on top of the units (spare, monitor engineer…). */
  bodypackCount: number;
  /** Extra bodypacks beyond the transmitter units (spare / monitor engineer / guest). */
  extraBodypacks: number;
  mixType: string;
  /** Who provides the IEM system: 'band' (we bring it) or 'venue' (we ask for it). */
  provider?: 'band' | 'venue';
  /** Who provides the WEDGES (returns) — 2026-10-17, user : venue (défaut) ou band. */
  wedgesProvider?: 'band' | 'venue';
  /** Who provides the SUBWOOFER — same choice, venue by default. */
  subProvider?: 'band' | 'venue';
  /** Frequency band used by the IEM system (e.g. "470-565 MHz (G-band)"). */
  frequencyRange?: string;
  /** Free-text note shown in the rider (e.g. frequencies to avoid, pack details). */
  notes?: string;
}

export function createDefaultIemConfig(): IemConfig {
  return { system: '', units: 0, bodypackCount: 0, extraBodypacks: 0, mixType: '', provider: 'venue', wedgesProvider: 'venue', subProvider: 'venue', frequencyRange: '', notes: '' };
}

export interface StageConfig {
  width: number;
  depth: number;
  risers: string;
  backlineItems: string[];
}

/**
 * Convert a StageConfig to a partial StagePlot StageDimensions.
 * Bridges the existing rider model with the new stage plot data model.
 * @see archived at ~/Hermes-Cloud/_archive/retrait-tech-rider-packages-2026-09-25.tar.gz
 *      (shared/types/stageplot.ts — the former @tech-rider/shared package, removed 2026-09-25)
 */
// stageConfigToDimensions REMOVED (audit 2026-10-24 [DEAD]): legacy bridge to the
// removed @tech-rider/shared package, never called. (createDefaultStageConfig stays
// — its shape is still documented here for the migration tests.)

export function createDefaultStageConfig(): StageConfig {
  return { width: 0, depth: 0, risers: '', backlineItems: [] };
}

export interface HospitalityConfig {
  catering: string[];
  drinks: string[];
  dressingRooms: string;
  security: string;
  dinner: string;
  /** Breakfast request — free text, echoed in the rider PDF when non-empty. (2026-08-21, Defaults-Spec P3b) */
  breakfast?: string;
  /** Lunch request — free text, echoed in the rider PDF when non-empty. (2026-08-21, Defaults-Spec P3b) */
  lunch?: string;
  /** Non-food allergies (cats/dogs, latex, dust…) — free text, echoed in the rider PDF. (2026-08-09) */
  nonFoodAllergies: string;
  /** Miscellaneous free-text rider note — echoed above SECURITY when non-empty. (2026-08-09) */
  various: string;
  /** Stage towels (on stage) — moved here from the per-person card (2026-08-07). */
  stageTowels: number;
  /** Dressing-room towels — moved here from the per-person card (2026-08-07). */
  dressingRoomTowels: number;
  /** Side-stage water bottles (2026-08-07). */
  stageWater: number;
}

export function createDefaultHospitalityConfig(): HospitalityConfig {
  return { catering: [], drinks: [], dressingRooms: '', security: '', dinner: '', breakfast: '', lunch: '', nonFoodAllergies: '', various: '', stageTowels: 0, dressingRoomTowels: 0, stageWater: 0 };
}

export type LightFixtureCategory =
  | 'movingHeads' | 'spots' | 'ledBars' | 'strobes'
  | 'washes' | 'blinders' | 'other';

export interface LightFixtureItem {
  id: string;
  type: string;        // suggestion auto-complétée (modèle, ex. "Clay Paky Sharpy")
  label: string;       // description courte
  quantity: number;
  notes: string;
}

export function createLightFixtureItem(overrides?: Partial<LightFixtureItem>): LightFixtureItem {
  return { id: crypto.randomUUID(), type: '', label: '', quantity: 1, notes: '', ...overrides };
}

export const LIGHT_FIXTURE_CATEGORIES: { value: LightFixtureCategory; label: string }[] = [
  { value: 'movingHeads', label: 'Moving Heads' },
  { value: 'spots', label: 'Spots' },
  { value: 'washes', label: 'Washes' },
  { value: 'ledBars', label: 'LED Bars' },
  { value: 'strobes', label: 'Strobes' },
  { value: 'blinders', label: 'Blinders' },
  { value: 'other', label: 'Other' },
];

export const LIGHT_FIXTURE_DEFAULTS: Record<LightFixtureCategory, string[]> = {
  movingHeads: ['Clay Paky Sharpy', 'Robe Spiider', 'Martin MAC Aura', 'GML Beam', 'Elation Platinum'],
  spots: ['ETC Source Four', 'Par Spot', 'Profile Spot', 'Beam Spot'],
  washes: ['Par Wash', 'RGBW Wash', 'LED Wash', 'Fresnel Wash'],
  ledBars: ['Pixel Bar', 'LED Batten', 'Mega Bar', 'Quad Color Bar'],
  strobes: ['LED Strobe', 'White Strobe', 'Tube Strobe'],
  blinders: ['Blinder 4-light', 'DWE Blinders', '8-light Blinder'],
  other: [],
};

export interface LightsFixtures {
  movingHeads: LightFixtureItem[];
  spots: LightFixtureItem[];
  ledBars: LightFixtureItem[];
  strobes: LightFixtureItem[];
  washes: LightFixtureItem[];
  blinders: LightFixtureItem[];
  other: LightFixtureItem[];
}

/** A reference to a light-plot FILE stored in the band file store
 * (`band_files`, category 'light_plot'). The lights config keeps ONLY this
 * small reference — never the binary / base64 — so a multi-MB PDF never
 * bloats the rider_config blob on every autosave or page load. Order in the
 * `plots` array = order of appearance in the rider PDF. (2026-10-06) */
export interface LightPlotRef {
  /** Stable ref id (uuid) — the UI list key. */
  id: string;
  /** `band_files.id` of the uploaded file. */
  fileId: number;
  /** Original file name — shown as the caption under the image in the PDF. */
  name: string;
  /** Stored mime type ('application/pdf', 'image/png', 'image/jpeg'…). */
  mime: string;
}

/** A light plot resolved to an embeddable PNG/JPEG data URL for the PDF
 * builder. `name` becomes the caption. (2026-10-06) */
export interface LightPlotRender {
  name?: string;
  dataUrl: string;
}

export interface LightsConfig {
  console: string;         // e.g. "GrandMA3"
  hazeMachine: string;     // e.g. "MDG Atme"
  fixtures: LightsFixtures;
  dmxLine: string;         // legacy DMX line note (string) — kept for back-compat
  dmxLines: number;        // number of DMX lines (UI "DMX Lines")
  dmxDistribution: string; // e.g. "5-pin XLR, daisy-chain"
  rigging: string;         // e.g. "Chainhoists, pipes, clamps"
  powerNotes: string;      // e.g. "separate line from sound"
  videoFormat: string;     // e.g. "HDMI 2.1", "DisplayPort 1.4"
  videoResolution: string; // e.g. "4K 60Hz", "Full HD"
  plotIncluded: boolean;   // whether light plot is pre-made
  notes: string;
  imageData?: string;      // legacy base64 image (lights plot/schematic) — kept working
  /** Ordered list of light-plot file references (PDFs / images). Optional:
   * absent = the legacy single-image behaviour (back-compat). (2026-10-06) */
  plots?: LightPlotRef[];
}

export function createDefaultLightsConfig(): LightsConfig {
  return {
    console: '',
    hazeMachine: '',
    fixtures: {
      movingHeads: [], spots: [], ledBars: [], strobes: [],
      washes: [], blinders: [], other: [],
    },
    dmxLine: '',
    dmxLines: 0,
    dmxDistribution: '',
    rigging: '',
    powerNotes: '',
    videoFormat: '',
    videoResolution: '',
    plotIncluded: false,
    notes: '',
  };
}

// BacklineItemType, BacklineItem, createBacklineItem are defined above (near Musician)

export interface BacklineConfig {
  items: BacklineItem[];
}

export function createDefaultBacklineConfig(): BacklineConfig {
  return {
    items: [],
  };
}

/** A single raised platform on stage (riser). Each riser is defined by its
 * name, dimensions (width/depth/height) and on-stage position. (2026-08-21,
 * Defaults-Spec P6 — structured list replaces the old free-form design.) */
export interface StageRiser {
  id: string;
  name: string;      // e.g. "Drum riser"
  width: string;     // e.g. "2m"
  depth: string;     // e.g. "1.5m"
  height: string;    // e.g. "0.4m"
  position: string;  // e.g. "centre back"
  /** Number of identical risers (quantity). Optional so legacy rows without
   * it still load; defaults to "1". (2026-08-26) */
  quantity?: string; // e.g. "2"
}

export function createEmptyStageRiser(): StageRiser {
  return { id: crypto.randomUUID(), name: '', width: '', depth: '', height: '', position: '', quantity: '1' };
}

/**
 * Stage Design — the band's descriptive stage-design notes (complementary to
 * the drawn Stage Plot, which stays untouched). Free-text fields + a structured
 * risers list. (2026-08-21, Defaults-Spec C7/P6 — rebuilt from the old
 * numeric/boolean model: backdropWidth/Height/pipe, rugType/Width/Depth/Quantity,
 * candlesQuantity/XlrCables/Power, chairsQuantity, toolsQuantity, stageplotImageData.)
 */
export interface StageDesignConfig {
  stageDimensions: string;      // e.g. "8m × 6m depth, 6m height" — IDEAL dimensions requested by the band
  backdrop: string;             // e.g. "Black backdrop, full-width, band logo optional"
  backdropDimensions: string;   // e.g. "6m × 3m, pipe at 5m"
  risers: StageRiser[];         // structured list of raised platforms
  rug: string;                  // e.g. "Black rug 4m × 2m in front of drum riser"
  accessories: string;          // e.g. "Gaffer tape, power strips, spare stands" (replaces candles — band-agnostic)
  chairs: string;               // e.g. "6 chairs in the wings"
  tools: string;                // e.g. "Spare cables, basic tool kit"
  notes: string;                // e.g. "Open to changes, confirm at get-in"
}

export function createDefaultStageDesignConfig(): StageDesignConfig {
  return {
    stageDimensions: '',
    backdrop: '',
    backdropDimensions: '',
    risers: [],
    rug: '',
    accessories: '',
    chairs: '',
    tools: '',
    notes: '',
  };
}

export interface CrewMember {
  id: string;
  name: string;
  role: CrewRole;
  /** Base role of the crew member: main (permanent) or session (deputy).
   * No guest for crew (2026-08-09). Default 'main'. */
  baseRole?: 'main' | 'session';
  // Multi tags per band (replaces the single-role select). The first tag is
  // mirrored into `role` for backward compatibility. `isManager` is kept only
  // as legacy: the Group Manager is now the 'manager' tag.
  tags?: string[];
  count: number;
  color: string;
  iemType: 'stereo' | 'mono' | 'none';
  /** true = wired in-ears (feed straight from the monitor rack): no transmitter
   *  and no bodypack to provide — excluded from the rider's "Units needed".
   *  `iemWiredProvidedBy` = WHO provides the wired pack (2026-10-17, user):
   *  'band' (default) or 'venue' — same semantics as Musician. */
  iemWired?: boolean;
  iemWiredProvidedBy?: ProvidedBy;
  wedges: number;
  subwoofer: boolean;
  // Monitor output routing (same semantics as Musician).
  iemRackId?: string;
  iemOutStart?: number;
  wedgeRackId?: string;
  wedgeOutStart?: number;
  wedgesStereo?: boolean;
  subRackId?: string;
  subOutStart?: number;
  diet: DietType;
  dietNotes: string;
  /** Non-food allergies (cats/dogs, latex, dust…) — mirrored from team_members (2026-08-26). */
  nonFoodAllergies?: string;
  towels: number;
  /** Dressing-room towels for this person (2026-08-07). */
  dressingRoomTowels: number;
  /** Water bottles for this person (side stage / dressing room) (2026-08-07). */
  water: number;
  backline: BacklineItem[];
  instruments: Instrument[];
  /** Display order within the crew list (drag & drop, 2026-08-09). */
  order?: number;
  // Personal Info (internal — not shown in the rider PDF)
  isManager?: boolean;
  realName?: string;
  lastName?: string;
  address?: string;
  postCode?: string;
  locality?: string;
  country?: string;
  phone?: string;
  email?: string;
  various?: string;
}

export function createCrewMember(overrides?: Partial<CrewMember>): CrewMember {
  return {
    id: crypto.randomUUID(),
    name: '',
    role: 'crew',
    baseRole: 'main',
    count: 1,
    color: '#818cf8',
    iemType: 'none',
    wedges: 0,
    subwoofer: false,
    diet: 'none',
    dietNotes: '',
    nonFoodAllergies: '',
        towels: 0,
        dressingRoomTowels: 1,
        water: 0,
        backline: [],
        tags: [],
        instruments: [],
        order: 0,
        isManager: false,
    realName: '',
    lastName: '',
    address: '',
    postCode: '',
    locality: '',
    country: '',
    phone: '',
    email: '',
    various: '',
    ...overrides,
  };
}

export interface GeneralConfig {
  /** Total show duration in minutes (new — Schedule page, 2026-08-08). */
  duration: number;
  soundcheckDurationMinutes: number;
  installationTimeMinutes: number;
  soundLimitDb: string;
  changeoverDurationMinutes: number;
  breakdownDurationMinutes: number;
  specialRequests: string;
  security: string;
  merchandise: string;
  notes: string;
  version: string;
}

export function createDefaultGeneralConfig(): GeneralConfig {
  return {
    duration: 0,
    soundcheckDurationMinutes: 0,
    installationTimeMinutes: 0,
    soundLimitDb: '',
    changeoverDurationMinutes: 0,
    breakdownDurationMinutes: 0,
    specialRequests: '',
    security: '',
    merchandise: '',
    notes: '',
    version: '',
  };
}

/** A named I/O rack (stagebox, console, side-stage…) that tracks can be
 * patched into. Defined in the band's Audio & Backline config; each track
 * selects one as its source (input) or destination (output). */
export interface StageRack {
  id: string;
  name: string;        // e.g. "Stagebox 1", "Console FOH", "Side Stage"
  inputs?: number;     // number of input channels available on this rack
  outputs?: number;    // number of output channels available on this rack
  notes?: string;
  /** SHOW EMPTY ROWS per rack (2026-10-18, user): when the rider's Input list
   *  pads empty rows, THIS rack's inputs are padded up to `inputs` only if true
   *  (undefined = true, back-compat). Lets a rack with few patched channels
   *  (e.g. a DL8 expander) stay compact while the main rack shows its holes. */
  padInputsInRider?: boolean;
  /** Same for the Output list padding (undefined = true). */
  padOutputsInRider?: boolean;
}

/** A shared side-fill monitor that serves the whole band (not one person).
 * Defined in the band's Audio & Backline config with its output routing. */
export interface SidefillConfig {
  id: string;
  name: string;        // e.g. "Sidefill L", "Sidefill R"
  rackId?: string;     // output rack
  channel?: number;    // start channel on that rack
}

export interface SpecsConfig {
  /** Microphone specs: models the band provides or requests from the venue. */
  microphonesSpecs: string;
  frequencyRange: string;           // e.g. "2000-626 MHz", "Sennheiser 2000 series"
  cableSpec: string;                // FOH digital/multi-channel cables (AES50/MADI/Dante) — "FOH Cables Specification"
  stageCableSpec: string;           // stage-level cabling — "Stage Cables Specification" (2026-08-13)
  audioCablesDetails: string;       // e.g. "DMX 512, 5-pin XLR, terminated" (audio DMX cabling)
  networkRequirements: string;      // e.g. "1Gbps switch", "fiber optic"
  powerRequirements: string;        // e.g. "230V 3-phase", "16A per circuit"
  stageRacks: StageRack[];          // named I/O destinations for tracks
  sidefills: SidefillConfig[];      // shared side-fill monitors for the band
  /** Wireless transmitters by instrument id, PER TRANSPORT preset — the signal
   * still enters wired (DI/mic); the transmitter is a separate rider request.
   * Provider (band/venue) + type + model may differ per transport, so the whole
   * transmitter definition lives in the rider config, not on the instrument
   * card (the card only shows a read-only badge). (2026-08-12) */
  wirelessTransmitters: Record<string, InstrumentWireless>;
  notes: string;
  /** Who provides each free-text spec line (2026-10-18) — per transport preset,
   * same model as iem.wedgesProvider. Undefined = band (no venue highlight). */
  microphonesSpecsProvider?: ProvidedBy;
  frequencyRangeProvider?: ProvidedBy;
  cableSpecProvider?: ProvidedBy;
  stageCableSpecProvider?: ProvidedBy;
  audioCablesDetailsProvider?: ProvidedBy;
  networkRequirementsProvider?: ProvidedBy;
  powerRequirementsProvider?: ProvidedBy;
}

export function createDefaultSpecsConfig(): SpecsConfig {
  return {
    microphonesSpecs: '',
    frequencyRange: '',
    cableSpec: '',
    stageCableSpec: '',
    audioCablesDetails: '',
    networkRequirements: '',
    powerRequirements: '',
    stageRacks: [
      { id: 'stagerack', name: 'Stagerack', inputs: 32, outputs: 16 },
      { id: 'console', name: 'Console', inputs: 16, outputs: 8 },
    ],
    sidefills: [
      { id: 'sidefill-sr', name: 'Sidefill SR' },
      { id: 'sidefill-sl', name: 'Sidefill SL' },
    ],
    wirelessTransmitters: {},
    notes: '',
  };
}

/** Merge the band-global racks with the ACTIVE transport preset's racks into
 * one de-duplicated list, first-named-wins. The band's default racks live in
 * `globalRacks` (band-level, transport-independent); per-transport
 * `specs[mode].stageRacks` holds only transport-specific exceptions. Used by
 * the instrument editors and the rider PDF so a track can target any rack the
 * band defined for the current transport. (2026-09-02, chantier #2) */
export function mergeStageRacks(
  globalRacks: StageRack[] | undefined,
  specs?: Partial<Record<TransportType, SpecsConfig>>,
  activeTransport?: TransportType,
): StageRack[] {
  const seen = new Map<string, StageRack>();
  const add = (list?: StageRack[]) => {
    if (!Array.isArray(list)) return;
    for (const r of list) {
      if (r && r.id && !seen.has(r.id)) seen.set(r.id, r);
    }
  };
  add(globalRacks);
  if (activeTransport) add(specs?.[activeTransport]?.stageRacks);
  const merged = Array.from(seen.values());
  // Fall back to sensible defaults when the band has not configured any rack
  // yet, so the instrument editors are usable out of the box.
  return merged.length > 0 ? merged : createDefaultSpecsConfig().stageRacks;
}

export interface RiderConfig {
  /** Active transport preset. '' = the band has not chosen one yet (wizard
   *  forces an explicit choice — Defaults-Spec P8). Never assume 'van'. */
  transport: TransportType | '';
  activeMusicianIds: string[];
  activeCrewIds: string[];
  /** Tracks which transport preset was last synced/active across sessions */
  lastSyncedTransport: TransportType | '';
  stagePlotImage?: string | null;
  /** Editable global contact-role suggestions for the band's shows. */
  contactRoles?: string[];
  /** Transport modes this band tours with (set by the Welcome wizard's
   *  Transport step). Optional — legacy configs omit it. */
  transports?: TransportType[];
}

/** A show-specific tech rider, assembled from site fields (10 sections scaled by headcount). */
export interface RiderInstance {
  id: string;
  band_id: string;
  name: string;
  transport: TransportType;
  active_musician_ids: string; // JSON array
  active_crew_ids: string;     // JSON array
  stage_plot_id?: string | null;
  requirements?: string | null; // JSON: 9 sections {sound,iem,stage,hospitality,lights,backline,stageDesign,general,specs}
  is_template?: number;
  source_rider_id?: string | null;
  musician_names?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function createDefaultRiderConfig(): RiderConfig {
  return {
    transport: '', activeMusicianIds: [], activeCrewIds: [], lastSyncedTransport: '',
    stagePlotImage: null,
    contactRoles: ['Stage manager', 'Backstage / hospitality', 'Runner', 'Artist liaison', 'Promoter', 'Driver', 'Security', 'Other'],
  };
}

export function createDefaultSectionBundle(): SectionBundle {
  return {
    sound: createDefaultSoundConfig(),
    iem: createDefaultIemConfig(),
    stage: createDefaultStageConfig(),
    hospitality: createDefaultHospitalityConfig(),
    lights: createDefaultLightsConfig(),
    backline: createDefaultBacklineConfig(),
    stageDesign: createDefaultStageDesignConfig(),
    general: createDefaultGeneralConfig(),
    specs: createDefaultSpecsConfig(),
  };
}

export const CONFIG_VERSION = 4;

export interface AppConfig {
  /** Band-global I/O racks (stagebox, console, side-stage…) shared across ALL
   * transport presets. The band's default racks live here; per-transport
   * `specs[mode].stageRacks` holds only transport-specific exceptions. A track
   * resolves its rackId against globalRacks first, then the active preset's
   * stageRacks. (2026-09-02, chantier #2 racks/inputs) */
  globalRacks: StageRack[];
  /** Version 3+ — each section has its own van/plane dual preset */
  sound: TransportConfig<SoundConfig>;
  iem: TransportConfig<IemConfig>;
  stage: TransportConfig<StageConfig>;
  hospitality: TransportConfig<HospitalityConfig>;
  lights: TransportConfig<LightsConfig>;
  backline: TransportConfig<BacklineConfig>;
  stageDesign: TransportConfig<StageDesignConfig>;
  general: TransportConfig<GeneralConfig>;
  specs: TransportConfig<SpecsConfig>;
  rider: RiderConfig;
  /** Per-band persisted setlist PDF settings (columns + style). */
  setlistPdf?: { columns: Record<string, boolean>; style: Record<string, unknown> };
  _version: number;
}

export function createDefaultAppConfig(): AppConfig {
  const def = createDefaultSectionBundle();
  return {
    globalRacks: [
      { id: 'stagerack', name: 'Stagerack', inputs: 32, outputs: 16 },
      { id: 'console', name: 'Console', inputs: 16, outputs: 8 },
    ],
    sound: { van: { ...def.sound }, plane: { ...def.sound }, tourbus: { ...def.sound }, train: { ...def.sound } },
    iem: { van: { ...def.iem }, plane: { ...def.iem }, tourbus: { ...def.iem }, train: { ...def.iem } },
    stage: { van: { ...def.stage }, plane: { ...def.stage }, tourbus: { ...def.stage }, train: { ...def.stage } },
    hospitality: { van: { ...def.hospitality }, plane: { ...def.hospitality }, tourbus: { ...def.hospitality }, train: { ...def.hospitality } },
    lights: { van: { ...def.lights }, plane: { ...def.lights }, tourbus: { ...def.lights }, train: { ...def.lights } },
    backline: { van: { ...def.backline }, plane: { ...def.backline }, tourbus: { ...def.backline }, train: { ...def.backline } },
    stageDesign: { van: { ...def.stageDesign }, plane: { ...def.stageDesign }, tourbus: { ...def.stageDesign }, train: { ...def.stageDesign } },
    general: { van: { ...def.general }, plane: { ...def.general }, tourbus: { ...def.general }, train: { ...def.general } },
    specs: { van: { ...def.specs }, plane: { ...def.specs }, tourbus: { ...def.specs }, train: { ...def.specs } },
    rider: createDefaultRiderConfig(),
    _version: CONFIG_VERSION,
  };
}

/**
 * Migrate an old single-config AppConfig (v1/v2) or the current v3 dual-preset
 * to the section-level-dual format. Handles:
 * - v1/v2: flat { sound, iem, stage, ... } — clones into both van/plane per section
 * - Current v3: { van: SectionBundle, plane: SectionBundle, ... } — unwraps into section-level
 */
/**
 * Ensure every section has a complete van/plane/tourbus bundle. Partial
 * stored configs (e.g. _version:3 with `general` missing the `tourbus` key)
 * must never reach the UI with an undefined mode — that crashes pages when
 * the user switches transport mode. Missing modes are backfilled from an
 * existing mode (van preferred, then plane), falling back to section defaults.
 */
const SECTION_FACTORIES: Record<string, () => any> = {
  sound: createDefaultSoundConfig,
  iem: createDefaultIemConfig,
  stage: createDefaultStageConfig,
  hospitality: createDefaultHospitalityConfig,
  lights: createDefaultLightsConfig,
  backline: createDefaultBacklineConfig,
  stageDesign: createDefaultStageDesignConfig,
  general: createDefaultGeneralConfig,
  specs: createDefaultSpecsConfig,
};

// A section's mode value must be a *leaf* section config (e.g. BacklineConfig
// { items: [...] }), NOT another transport envelope. Older builds could persist
// a transport mode whose value is itself a bundle ({ van:{...}, plane:{...},
// tourbus:{...} }) — normalize kept it because it is truthy, leaving e.g.
// `backline.train` without `.items`, so ConfigAudioBacklinePage crashed with
// "Cannot read properties of undefined (reading 'length')" when the user
// switched to that mode. Any value carrying transport keys is treated as an
// invalid envelope and falls back to the previous valid mode. (2026-09-02)
const MODE_KEYS = ['van', 'plane', 'tourbus', 'train'];
const isModeEnvelope = (v: any) =>
  !!v && typeof v === 'object' && !Array.isArray(v) &&
  MODE_KEYS.some((k) => Object.prototype.hasOwnProperty.call(v, k));

function normalizeAppConfig(cfg: any): AppConfig {
  const SECTIONS = ['sound', 'iem', 'stage', 'hospitality', 'lights', 'backline', 'stageDesign', 'general', 'specs'];
  // Dead section: PowerConfig was removed (its real counterpart is
  // specs.powerRequirements). Drop any residual `power` key stored in the blob
  // so no orphaned/unsupported data survives a load+save cycle. (Defaults-Spec C7)
  delete cfg.power;
  for (const s of SECTIONS) {
    const sec = cfg[s] || {};
    const def = SECTION_FACTORIES[s] ? SECTION_FACTORIES[s]() : {};
    // Keep a mode only if it is a real leaf section; otherwise fall back to the
    // previous valid mode so the UI never reads an undefined `.items`/field.
    const pick = (k: string, fb: any) => {
      const v = sec[k];
      return v && !isModeEnvelope(v) ? v : fb;
    };
    const van = pick('van', def);
    const plane = pick('plane', van);
    const tourbus = pick('tourbus', van);
    const train = pick('train', plane);
    cfg[s] = { van, plane, tourbus, train };
  }
  // Backfill the sound `intro` field for configs saved before it existed
  // (SoundConfig.intro is required; the default is the legal intro sentence).
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const s = cfg.sound?.[t];
    if (s && typeof s === 'object' && s.intro == null) {
      s.intro = createDefaultSoundConfig().intro;
    }
  }
  // Backfill the hospitality `breakfast`/`lunch` fields for configs saved
  // before they existed (Defaults-Spec P3b). They're optional in the type, but
  // this makes the stored config self-consistent so the rider/share reads treat
  // them like the other free-text fields (empty string).
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const h = cfg.hospitality?.[t];
    if (h && typeof h === 'object') {
      if (h.breakfast == null) h.breakfast = '';
      if (h.lunch == null) h.lunch = '';
    }
  }
  // Stage Design (Defaults-Spec P6): the config was rebuilt from a numeric/
  // boolean model (backdropWidth/Height/pipe, rugType/Width/Depth/Quantity,
  // candlesQuantity/XlrCables/Power, chairsQuantity, toolsQuantity,
  // stageplotImageData) to a free-text + structured-risers model. Drop the old
  // fields and backfill the new ones so the UI/PDF never read a missing key.
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const sd = cfg.stageDesign?.[t];
    if (!sd || typeof sd !== 'object' || Array.isArray(sd)) continue;
    for (const legacy of ['backdropWidth', 'backdropHeight', 'backdropPipe', 'rugType', 'rugWidth', 'rugDepth', 'rugQuantity', 'candlesQuantity', 'candlesXlrCables', 'candlesPower', 'chairsQuantity', 'toolsQuantity', 'stageplotImageData']) {
      delete sd[legacy];
    }
    if (sd.stageDimensions == null) sd.stageDimensions = '';
    if (sd.backdrop == null) sd.backdrop = '';
    if (sd.backdropDimensions == null) sd.backdropDimensions = '';
    if (!Array.isArray(sd.risers)) sd.risers = [];
    if (sd.rug == null) sd.rug = '';
    if (sd.accessories == null) sd.accessories = '';
    if (sd.chairs == null) sd.chairs = '';
    if (sd.tools == null) sd.tools = '';
    if (sd.notes == null) sd.notes = '';
  }
  // One-time relocation: Video fields moved from Technical Specs to Lights.
  // Copy any existing values so stored data follows the move; stale specs
  // keys are ignored by the SpecsConfig type and dropped on next save.
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const src = cfg.specs?.[t];
    const dst = cfg.lights?.[t];
    if (src && dst) {
      if (!dst.videoFormat && src.videoFormat) dst.videoFormat = src.videoFormat;
      if (!dst.videoResolution && src.videoResolution) dst.videoResolution = src.videoResolution;
    }
  }
  // Rename: specs[mode].dmxChainDetails -> audioCablesDetails (audio DMX cabling,
  // distinct from Lights DMX). Carry existing values forward.
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const sec = cfg.specs?.[t];
    if (sec && sec.dmxChainDetails != null && !sec.audioCablesDetails) {
      sec.audioCablesDetails = sec.dmxChainDetails;
    }
    if (sec) delete sec.dmxChainDetails;
    // Backfill specs fields added after a band's config was saved (2026-08-13).
    if (sec && sec.stageCableSpec == null) sec.stageCableSpec = '';
  }
  // Wireless transmitters: backfill the 2 distinct providers (mic + receiver)
  // from the legacy single `providedBy` value saved before they were split.
  // If providedBy was set, both sides take that value; otherwise each defaults
  // to 'band'. (2026-08-21, Defaults-Spec C1)
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const wts = cfg.specs?.[t]?.wirelessTransmitters;
    if (!wts || typeof wts !== 'object' || Array.isArray(wts)) continue;
    for (const instId of Object.keys(wts)) {
      const w = wts[instId];
      if (!w || typeof w !== 'object') continue;
      if (w.micProvider == null && w.receiverProvider == null && w.providedBy != null) {
        w.micProvider = w.providedBy;
        w.receiverProvider = w.providedBy;
      }
      if (w.micProvider == null) w.micProvider = 'band';
      if (w.receiverProvider == null) w.receiverProvider = 'band';
    }
  }
  // Lights fixtures: old model stored plain numbers per category
  // ({spots:12, washes:6, blinders:4, frontTrussSpots:2}); the new model stores
  // an editable ITEM LIST per category (like backline). Migrate numbers into a
  // single item each so no existing light request is lost. (2026-08-13)
  // The legacy `frontTrussSpots` numeric had no UI counterpart any more (the
  // Front Truss category was retired — it duplicated the other fixture lists,
  // 2026-09-27) so it is no longer migrated — but any list stored under a
  // retired category is CARRIED OVER untouched below, never dropped by a
  // load+save cycle.
  const FIXTURE_MIGRATE: Record<string, keyof LightsFixtures> = {
    spots: 'spots', washes: 'washes', blinders: 'blinders',
  };
  for (const t of ['van', 'plane', 'tourbus', 'train']) {
    const l = cfg.lights?.[t];
    if (!l || typeof l !== 'object' || !l.fixtures || Array.isArray(l.fixtures)) continue;
    const fx: any = l.fixtures;
    const next: any = createDefaultLightsConfig().fixtures;
    // Carry over EVERY list category present in the saved blob — including the
    // keys that are no longer part of the UI/type (e.g. the retired
    // `frontTruss`) — so their stored items survive a load+save cycle. Then
    // migrate the old numeric ones.
    for (const key of new Set([...Object.keys(next), ...Object.keys(fx)])) {
      if (Array.isArray(fx[key])) next[key] = fx[key];
    }
    for (const [oldKey, newKey] of Object.entries(FIXTURE_MIGRATE)) {
      const n = Number(fx[oldKey] ?? 0);
      if (n > 0 && !Array.isArray(fx[newKey])) {
        next[newKey] = [{ id: crypto.randomUUID(), type: '', label: '', quantity: n, notes: '' }];
      }
    }
    l.fixtures = next;
  }
  // Band-global I/O racks (chantier #2, 2026-09-02): promote every rack defined
  // in the per-transport presets into a single band-global list (dedup by id,
  // first-wins). Per-transport stageRacks then keep only racks that were NOT
  // promoted — i.e. genuine transport-specific exceptions. A config that never
  // defined racks falls back to the 2 default global racks. Idempotent: a
  // post-migration config already has globalRacks set and empty per-transport
  // lists, so this leaves it untouched.
  const rackCount = new Map<string, number>();
    const rackById = new Map<string, StageRack>();
    for (const t of ['van', 'plane', 'tourbus', 'train']) {
      const list = cfg.specs?.[t]?.stageRacks;
      if (!Array.isArray(list)) continue;
      for (const r of list) {
        if (!r || !r.id) continue;
        rackCount.set(r.id, (rackCount.get(r.id) || 0) + 1);
        if (!rackById.has(r.id)) rackById.set(r.id, r);
      }
    }
    // A rack is global when it was already in cfg.globalRacks OR is shared by
    // more than one transport preset. A rack defined by a single preset is a
    // genuine transport-specific exception and stays per-transport.
    const globalIds = new Set<string>();
    for (const r of cfg.globalRacks || []) if (r && r.id) globalIds.add(r.id);
    for (const [id, count] of rackCount) if (count > 1) globalIds.add(id);
    const globalRacks: StageRack[] = [];
    const seen = new Set<string>();
    for (const r of cfg.globalRacks || []) if (r && r.id && !seen.has(r.id)) { seen.add(r.id); globalRacks.push(r); }
    for (const [id, r] of rackById) if (globalIds.has(id) && !seen.has(id)) { seen.add(id); globalRacks.push(r); }
    cfg.globalRacks = globalRacks.length > 0 ? globalRacks : createDefaultSpecsConfig().stageRacks;
    for (const t of ['van', 'plane', 'tourbus', 'train']) {
      const sec = cfg.specs?.[t];
      if (sec && Array.isArray(sec.stageRacks)) {
        sec.stageRacks = sec.stageRacks.filter((r: StageRack) => r && r.id && !globalIds.has(r.id));
      }
    }
  // Guarantee a complete rider config: a legacy/partial row (e.g. only
  // contactRoles) must never leave the required arrays undefined — RiderPage
  // crashes on `activeMusicianIds.length`/`.includes`. Merge the defaults in.
  cfg.rider = { ...createDefaultRiderConfig(), ...(cfg.rider || {}) };
  return cfg as AppConfig;
}

export function migrateAppConfig(raw: Record<string, unknown>): AppConfig {
  // Defensive: if a transport section is itself a transport-bundle
  // (over-nested, e.g. { van: { van: {...}, plane: {...} } } from an older
  // code version), flatten it so config[section].van holds the real data.
  const flattenSection = (sec: any): any => {
    if (sec && typeof sec === 'object' && !Array.isArray(sec)
        && sec.van && typeof sec.van === 'object' && !Array.isArray(sec.van)
        && 'van' in sec.van && 'plane' in sec.van) {
      const out: any = {};
      for (const t of ['van', 'plane', 'tourbus', 'train']) {
        out[t] = (sec[t] && sec[t][t]) ? sec[t][t] : (sec.van[t] || sec[t] || {});
      }
      return out;
    }
    return sec;
  };

  if (raw._version === CONFIG_VERSION) {
    // Already the target format — but still flatten any over-nested sections
    // so legacy data saved by older builds can't crash the UI.
    const cloned: any = { ...raw };
    for (const s of ['sound', 'iem', 'stage', 'hospitality', 'lights', 'backline', 'stageDesign', 'general', 'specs']) {
      if (cloned[s]) cloned[s] = flattenSection(cloned[s]);
    }
    return normalizeAppConfig(cloned);
  }

  // Detect if it's the current dual-preset format (van/plane at top level)
  if ('van' in raw && 'plane' in raw) {
    const van = raw.van as Record<string, unknown>;
    const plane = raw.plane as Record<string, unknown>;
    const rider: RiderConfig = (raw.rider as RiderConfig) ?? createDefaultRiderConfig();

    const generalVan: GeneralConfig = (van.general as GeneralConfig) ?? createDefaultGeneralConfig();
    const generalPlane: GeneralConfig = (plane.general as GeneralConfig) ?? createDefaultGeneralConfig();
    const generalTourbus: GeneralConfig = ((van.tourbus as GeneralConfig) ?? (plane.tourbus as GeneralConfig) ?? (van.general as GeneralConfig) ?? createDefaultGeneralConfig());
    // crew migrated out of config -> rider_crew table; drop any residual crew key
    delete (generalVan as any).crew;
    delete (generalPlane as any).crew;
    delete (generalTourbus as any).crew;

    return normalizeAppConfig({
      sound: { van: (van.sound as SoundConfig) ?? createDefaultSoundConfig(), plane: (plane.sound as SoundConfig) ?? createDefaultSoundConfig(), tourbus: ((van.tourbus as SoundConfig) ?? (plane.tourbus as SoundConfig) ?? (van.sound as SoundConfig) ?? createDefaultSoundConfig()) },
      iem: { van: (van.iem as IemConfig) ?? createDefaultIemConfig(), plane: (plane.iem as IemConfig) ?? createDefaultIemConfig(), tourbus: ((van.tourbus as IemConfig) ?? (plane.tourbus as IemConfig) ?? (van.iem as IemConfig) ?? createDefaultIemConfig()) },
      stage: { van: (van.stage as StageConfig) ?? createDefaultStageConfig(), plane: (plane.stage as StageConfig) ?? createDefaultStageConfig(), tourbus: ((van.tourbus as StageConfig) ?? (plane.tourbus as StageConfig) ?? (van.stage as StageConfig) ?? createDefaultStageConfig()) },
      hospitality: { van: (van.hospitality as HospitalityConfig) ?? createDefaultHospitalityConfig(), plane: (plane.hospitality as HospitalityConfig) ?? createDefaultHospitalityConfig(), tourbus: ((van.tourbus as HospitalityConfig) ?? (plane.tourbus as HospitalityConfig) ?? (van.hospitality as HospitalityConfig) ?? createDefaultHospitalityConfig()) },
      lights: { van: (van.lights as LightsConfig) ?? createDefaultLightsConfig(), plane: (plane.lights as LightsConfig) ?? createDefaultLightsConfig(), tourbus: ((van.tourbus as LightsConfig) ?? (plane.tourbus as LightsConfig) ?? (van.lights as LightsConfig) ?? createDefaultLightsConfig()) },
      backline: { van: (van.backline as BacklineConfig) ?? createDefaultBacklineConfig(), plane: (plane.backline as BacklineConfig) ?? createDefaultBacklineConfig(), tourbus: ((van.tourbus as BacklineConfig) ?? (plane.tourbus as BacklineConfig) ?? (van.backline as BacklineConfig) ?? createDefaultBacklineConfig()) },
      stageDesign: { van: (van.stageDesign as StageDesignConfig) ?? createDefaultStageDesignConfig(), plane: (plane.stageDesign as StageDesignConfig) ?? createDefaultStageDesignConfig(), tourbus: ((van.tourbus as StageDesignConfig) ?? (plane.tourbus as StageDesignConfig) ?? (van.stageDesign as StageDesignConfig) ?? createDefaultStageDesignConfig()) },
      general: { van: generalVan, plane: generalPlane, tourbus: generalTourbus },
      specs: { van: (van.specs as SpecsConfig) ?? createDefaultSpecsConfig(), plane: (plane.specs as SpecsConfig) ?? createDefaultSpecsConfig(), tourbus: ((van.tourbus as SpecsConfig) ?? (plane.tourbus as SpecsConfig) ?? (van.specs as SpecsConfig) ?? createDefaultSpecsConfig()) },
      rider,
      _version: CONFIG_VERSION,
    });
  }

  // v1/v2 format: flat single-config — clone into both presets per section
  const transportCfg: SectionBundle = {
    sound: (raw.sound as SoundConfig) ?? createDefaultSoundConfig(),
    iem: (raw.iem as IemConfig) ?? createDefaultIemConfig(),
    stage: (raw.stage as StageConfig) ?? createDefaultStageConfig(),
    hospitality: (raw.hospitality as HospitalityConfig) ?? createDefaultHospitalityConfig(),
    lights: (raw.lights as LightsConfig) ?? createDefaultLightsConfig(),
    backline: (raw.backline as BacklineConfig) ?? createDefaultBacklineConfig(),
    stageDesign: (raw.stageDesign as StageDesignConfig) ?? createDefaultStageDesignConfig(),
    general: (raw.general as GeneralConfig) ?? createDefaultGeneralConfig(),
    specs: (raw.specs as SpecsConfig) ?? createDefaultSpecsConfig(),
  };
  // crew migrated out of config -> rider_crew table; drop any residual crew key
  delete (transportCfg.general as any).crew;

  const rider: RiderConfig = (raw.rider as RiderConfig) ?? createDefaultRiderConfig();

  return normalizeAppConfig({
    sound: { van: { ...transportCfg.sound }, plane: { ...transportCfg.sound }, tourbus: { ...transportCfg.sound } },
    iem: { van: { ...transportCfg.iem }, plane: { ...transportCfg.iem }, tourbus: { ...transportCfg.iem } },
    stage: { van: { ...transportCfg.stage }, plane: { ...transportCfg.stage }, tourbus: { ...transportCfg.stage } },
    hospitality: { van: { ...transportCfg.hospitality }, plane: { ...transportCfg.hospitality }, tourbus: { ...transportCfg.hospitality } },
    lights: { van: { ...transportCfg.lights }, plane: { ...transportCfg.lights }, tourbus: { ...transportCfg.lights } },
    backline: { van: { ...transportCfg.backline }, plane: { ...transportCfg.backline }, tourbus: { ...transportCfg.backline } },
    stageDesign: { van: { ...transportCfg.stageDesign }, plane: { ...transportCfg.stageDesign }, tourbus: { ...transportCfg.stageDesign } },
    general: { van: { ...transportCfg.general }, plane: { ...transportCfg.general }, tourbus: { ...transportCfg.general } },
    specs: { van: { ...transportCfg.specs }, plane: { ...transportCfg.specs }, tourbus: { ...transportCfg.specs } },
    rider,
    _version: CONFIG_VERSION,
  });
}

export interface BandProfile {
  id: string;
  name: string;
  slug?: string;
  image?: string;
  color?: string;
  /** Home country of the band — drives timezone offset & currency on the day sheet. */
  country?: string;
  /** Home base (2026-10-11) — the van's departure point for the automatic
      « band -> venue » distance leg. lat/lng are filled by the geocode route. */
  address?: string;
  postal_code?: string;
  /** Aéroport de préférence (2026-10-17) : défaut des travel days en mode plane. */
  default_airport?: string;
  /** Gare de préférence (2026-11-02, LOT U2c) : jambes train (code UIC). */
  default_station?: string;
  city?: string;
  lat?: number | null;
  lng?: number | null;
  lastModified: number;
  schemaVersion: number;
  /** The current user's role on this band. Returned by the API; the sidebar uses
      it to hide links the role cannot use. `tech` (renamed from `crew`, 2026-10-08)
      and `crew` (legacy rows awaiting migration) belong to the union too. */
  my_role?: 'owner' | 'editor' | 'tech' | 'crew' | 'viewer';
  /** Whether the current user has FINANCE access on this band (owner/superadmin
      always true; editor/viewer only if granted by the owner). (2026-08-23) */
  finance_access?: boolean;
  /** Whether the current user has LAYOUT access on this band (owner/superadmin
      always true; editor/viewer only if granted by the owner) — gates the 3 PDF
      layout editors. Raw `user_bands.layout_access` flag (0/1). (2026-09-24)
      LEGACY derived value: the capability matrix below is authoritative. */
  layout_access?: number;
  /** The caller's EFFECTIVE capability matrix on this band (chantier C,
      2026-09-25): the delegable capabilities granted to the signed-in user
      (owner/superadmin implicitly hold all 7). Names are frozen — see
      `src/lib/permissions.ts`. Absent on a pre-matrix payload. */
  permissions?: string[];
  /** Read capabilities EXPLICITLY DENIED to the caller on this band
      (`<family>.view` stored `granted = 0`) — the « — » level of the page×level
      grid (2026-10-08). Distinct from an ABSENT grant, which keeps the historical
      behaviour (reads allowed): the sidebar and the preview read it through
      `src/lib/pageAccess.ts`. */
  denied?: string[];
}

export function createBandProfile(overrides?: Partial<BandProfile>): BandProfile {
  return {
    id: crypto.randomUUID(),
    name: 'New Band',
    slug: overrides?.slug || '',
    image: '',
    color: '#e94560',
    country: overrides?.country || '',
    address: overrides?.address || '',
    postal_code: overrides?.postal_code || '',
    city: overrides?.city || '',
    lat: overrides?.lat ?? null,
    lng: overrides?.lng ?? null,
    lastModified: Date.now(),
    schemaVersion: 2,
    ...overrides,
  };
}

export const PROFILE_STORAGE_KEY = 'tech-rider-profiles';
export const STORAGE_VERSION = 2;
